"use client";

import { Box } from "@mui/material";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { toAssetImages } from "src/lib/utils";
import Editor from "../editor";

export interface VariantProps {
  /** @default "" */
  content: string;
  /** @default "" */
  imageUrl?: string;
  /** @default "Back to Templates" */
  backLinkText?: string;
  /** @default "" */
  title?: string;
  /** @default "" */
  description?: string;
}

const MarkdownDefault = (props: VariantProps) => {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window !== "undefined") {
      // Inject global styles for recipe tags and meta
      const styleId = "recipe-styles";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          .recipe-tags {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 8px !important;
            margin-bottom: 16px !important;
          }
          .recipe-tags .tag.badge {
            display: inline-flex !important;
            align-items: center !important;
            padding: 0.375rem 0.75rem !important;
            border-radius: 1rem !important;
            font-size: 0.875rem !important;
            font-weight: 500 !important;
            line-height: 1.5 !important;
            transition: all 0.2s ease !important;
          }
          .recipe-tags .tag.badge.cuisine-type {
            background-color: rgba(25, 118, 210, 0.1) !important;
            color: rgb(25, 118, 210) !important;
            border: 1px solid rgba(25, 118, 210, 0.3) !important;
          }
          .recipe-tags .tag.badge.meal-type {
            background-color: rgba(156, 39, 176, 0.1) !important;
            color: rgb(156, 39, 176) !important;
            border: 1px solid rgba(156, 39, 176, 0.3) !important;
          }
          .recipe-tags .tag.badge.difficulty {
            background-color: rgba(255, 152, 0, 0.1) !important;
            color: rgb(255, 152, 0) !important;
            border: 1px solid rgba(255, 152, 0, 0.3) !important;
          }
          .recipe-tags .tag.badge.cooking-method {
            background-color: rgba(76, 175, 80, 0.1) !important;
            color: rgb(76, 175, 80) !important;
            border: 1px solid rgba(76, 175, 80, 0.3) !important;
          }
          .recipe-tags .tag.badge.meal-event {
            background-color: rgba(158, 158, 158, 0.1) !important;
            color: rgb(97, 97, 97) !important;
            border: 1px solid rgba(158, 158, 158, 0.3) !important;
          }
          .recipe-meta {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 16px !important;
            margin-bottom: 16px !important;
            padding: 12px !important;
            background-color: rgba(0, 0, 0, 0.02) !important;
            border-radius: 4px !important;
          }
          .recipe-meta > span {
            display: inline-flex !important;
            align-items: center !important;
            gap: 4px !important;
            font-size: 0.875rem !important;
            font-weight: 500 !important;
          }
          .recipe-meta span[data-field]::before {
            content: attr(data-field) ": " !important;
            font-weight: 600 !important;
            color: inherit !important;
            margin-right: 0 !important;
          }
          .recipe-meta span.prep-time::after,
          .recipe-meta span.cook-time::after {
            content: " phút" !important;
          }
          .recipe-meta span.servings::after {
            content: " người" !important;
          }
          .recipe-meta span[data-field="prep-time"]::after,
          .recipe-meta span[data-field="cook-time"]::after {
            content: " phút" !important;
          }
          .recipe-meta span[data-field="servings"]::after {
            content: " người" !important;
          }
        `;
        document.head.appendChild(style);
      }
    }
  }, [pathname]);

  // Post-process the rendered HTML to fix recipe tags and meta
  useEffect(() => {
    if (typeof window === "undefined" || !props.content) return;

    const fixRecipeHTML = () => {
      // Extract original HTML from content
      const parser = new DOMParser();
      const doc = parser.parseFromString(props.content, "text/html");

      // Find recipe-tags in original HTML
      const originalRecipeTags = doc.querySelector(".recipe-tags");
      if (originalRecipeTags) {
        const originalHTML = originalRecipeTags.outerHTML;
        const recipeTagsDivs = document.querySelectorAll(
          ".ProseMirror .recipe-tags, .markdown-editor-wrapper .recipe-tags",
        );
        recipeTagsDivs.forEach((div) => {
          // Check if structure is lost (no spans)
          if (!div.querySelector("span.tag")) {
            // Restore the original HTML structure
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = originalHTML;
            const restoredDiv = tempDiv.querySelector(".recipe-tags");
            if (restoredDiv) {
              div.innerHTML = restoredDiv.innerHTML;
            }
          }
        });
      }

      // Find recipe-meta in original HTML
      const originalRecipeMeta = doc.querySelector(".recipe-meta");
      if (originalRecipeMeta) {
        const originalHTML = originalRecipeMeta.outerHTML;
        const recipeMetaDivs = document.querySelectorAll(
          ".ProseMirror .recipe-meta, .markdown-editor-wrapper .recipe-meta",
        );
        recipeMetaDivs.forEach((div) => {
          // Check if structure is lost (no spans)
          if (!div.querySelector("span.prep-time, span.cook-time, span.servings")) {
            // Restore the original HTML structure
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = originalHTML;
            const restoredDiv = tempDiv.querySelector(".recipe-meta");
            if (restoredDiv) {
              div.innerHTML = restoredDiv.innerHTML;
            }
          }
        });
      }
    };

    // Run after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(fixRecipeHTML, 200);

    // Also observe for changes
    const observer = new MutationObserver(() => {
      setTimeout(fixRecipeHTML, 50);
    });
    const proseMirror = document.querySelector(".ProseMirror");
    if (proseMirror) {
      observer.observe(proseMirror, { childList: true, subtree: true });
    }

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [props.content]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {props.imageUrl && (
        <Box
          sx={{
            width: "70%",
            position: "relative",
            marginBottom: 4,
            borderRadius: 2,
            overflow: "hidden",
            aspectRatio: { xs: "16/9", sm: "21/9", md: "21/9" },
            "& img": {
              objectFit: "cover",
            },
          }}
        >
          <Image
            src={toAssetImages(props.imageUrl)}
            alt="Article header image"
            fill
            priority
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 70vw"
            style={{
              objectFit: "cover",
            }}
          />
        </Box>
      )}
      <Box
        className="markdown-editor-wrapper"
        sx={{
          width: "70%",
          "& > *": {
            width: "100% !important",
            margin: "0 !important",
            maxWidth: "100% !important",
          },
          "& > *:first-of-type": {
            width: "100% !important",
            margin: "0 !important",
            maxWidth: "100% !important",
          },
          // Recipe tags styles - target ProseMirror content
          "& .ProseMirror .recipe-tags, & .recipe-tags": {
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            marginBottom: 2,
            "& .tag.badge": {
              display: "inline-flex",
              alignItems: "center",
              padding: "0.375rem 0.75rem",
              borderRadius: "1rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              lineHeight: 1.5,
              transition: "all 0.2s ease",
              "&.cuisine-type": {
                backgroundColor: "rgba(25, 118, 210, 0.1)",
                color: "rgb(25, 118, 210)",
                border: "1px solid rgba(25, 118, 210, 0.3)",
              },
              "&.meal-type": {
                backgroundColor: "rgba(156, 39, 176, 0.1)",
                color: "rgb(156, 39, 176)",
                border: "1px solid rgba(156, 39, 176, 0.3)",
              },
              "&.difficulty": {
                backgroundColor: "rgba(255, 152, 0, 0.1)",
                color: "rgb(255, 152, 0)",
                border: "1px solid rgba(255, 152, 0, 0.3)",
              },
              "&.cooking-method": {
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                color: "rgb(76, 175, 80)",
                border: "1px solid rgba(76, 175, 80, 0.3)",
              },
              "&.meal-event": {
                backgroundColor: "rgba(158, 158, 158, 0.1)",
                color: "rgb(97, 97, 97)",
                border: "1px solid rgba(158, 158, 158, 0.3)",
              },
            },
          },
          // Recipe meta styles - target ProseMirror content
          "& .ProseMirror .recipe-meta, & .recipe-meta": {
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            marginBottom: 2,
            padding: 1.5,
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            borderRadius: 1,
            "& > span": {
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: "0.875rem",
              color: "text.secondary",
              fontWeight: 500,
            },
            "& span[data-field]": {
              "&::before": {
                content: 'attr(data-field) ": "',
                fontWeight: 600,
                color: "text.primary",
                marginRight: 0,
              },
            },
            "& span.prep-time, & span.cook-time": {
              "&::after": {
                content: '" phút"',
              },
            },
            "& span.servings": {
              "&::after": {
                content: '" người"',
              },
            },
            "& span[data-field='prep-time'], & span[data-field='cook-time']": {
              "&::after": {
                content: '" phút"',
              },
            },
            "& span[data-field='servings']": {
              "&::after": {
                content: '" người"',
              },
            },
          },
        }}
      >
        <Editor content={props.content} slug="" />
      </Box>
    </Box>
  );
};

export default MarkdownDefault;
